import {
  Autocomplete,
  Dialog,
  Form,
  FormButton,
  useNotifications
} from '@ijoin/components';
import {
  getGetAdminPlanAdvisorsQueryKey,
  usePostAdminPlanAdvisor
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '../types';

type Option = { label: string; value: string };

export const AddPlanAdvisorDialog: FC<{
  advisorOptions: Option[];
  onClose: () => void;
  planId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<{ advisor: null | Option }>({
    defaultValues: { advisor: null }
  });
  const advisor = methods.watch('advisor');

  const addAdvisor = usePostAdminPlanAdvisor<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to add advisor to plan'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminPlanAdvisorsQueryKey(props.planId)
        });
        notifications.success('Advisor added to plan');
        props.onClose();
      }
    }
  });

  const onSubmit = (data: { advisor: null | Option }) => {
    if (!data.advisor) return;

    addAdvisor.mutate({
      data: { advisorId: data.advisor.value },
      planId: props.planId
    });
  };

  return (
    <Dialog
      data-testid='addPlanAdvisorDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Add Advisor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete<Option>
              data-testid='advisorToAdd'
              fullWidth
              hideError
              label='Advisor'
              name='advisor'
              options={props.advisorOptions}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelAddPlanAdvisor'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='submitAddPlanAdvisor'
            disabled={!advisor || addAdvisor.isPending}
            variant='contained'>
            Submit
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

AddPlanAdvisorDialog.displayName = 'AddPlanAdvisorDialog';
